import React, { useState } from "react";
import { Button, Alert, Card } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/en";

import weekday from "dayjs/plugin/weekday";
import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaPhone,
  FaTimesCircle,
} from "react-icons/fa";
import { FaRegCalendarDays } from "react-icons/fa6";
import { TiTime } from "react-icons/ti";
import { BsClock } from "react-icons/bs";
import { useLanguage } from "../../context/LanguageContext";
import { useNavigate, useParams } from "react-router-dom";

dayjs.extend(weekday);

export default function DateSelect({ workingDays, onSelect, onBack }) {
  const { t, currentLanguage } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const isWorkingDay = (date) => {
    const dayOfWeek = date.day();
    const workingDay = workingDays.find((day) => day.dayOfWeek === dayOfWeek);
    return workingDay?.isWorking || false;
  };

  const disabledDate = (current) => {
    if (current && current < dayjs().startOf("day")) {
      return true;
    }
    return !isWorkingDay(current);
  };

  const handleDateSelect = (date) => {
    const dayOfWeek = date.day();
    const workingDay = workingDays.find((day) => day.dayOfWeek === dayOfWeek);

    if (!workingDay || !workingDay.isWorking) {
      setError(t("dateSelect.error.notAvailable"));
      return;
    }

    setSelectedDate(date);
    setError("");
    onSelect(date.toISOString()); // Auto-progress after selection
  };

  // Generate calendar days for the current month
  const startOfMonth = currentMonth.startOf("month");
  const endOfMonth = currentMonth.endOf("month");
  const startDay = startOfMonth.day();
  const daysInMonth = endOfMonth.date();
  const days = [];

  // Add empty slots for days before the 1st of the month
  for (let i = 0; i < startDay; i++) {
    days.push(null);
  }

  // Add actual days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    const date = startOfMonth.set("date", i);
    days.push(date);
  }

  // Add empty slots after the last day to fill the grid
  const remainingSlots = 42 - days.length; // 6 rows x 7 columns
  for (let i = 0; i < remainingSlots; i++) {
    days.push(null);
  }

  if (workingDays.length === 0) {
    return (
      <Card className="border-2 border-dashed border-placeHolder bg-cardColor rounded-xl">
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center gap-4">
          {/* Icon */}
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <BsClock className="text-3xl text-primary" />
          </div>

          <div className="flex items-center gap-2">
            <div className="h-px w-8 bg-gray-300" />
            <span className="text-[11px] font-semibold tracking-widest uppercase text-primary">
              {t("dateSelect.noSchedule.comingSoon")}
            </span>
            <div className="h-px w-8 bg-gray-300" />
          </div>

          {/* Pills */}
          <div className="flex flex-wrap justify-center gap-2 mt-1">
            <span className="bg-gray-100  text-gray-400 text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <FaCalendarAlt className="text-gray-400" size={11} />
              {t("dateSelect.noSchedule.checkLater")}
            </span>
            <span
              onClick={() => navigate(`/contact-us`)}
              className="bg-primary/20 text-primary text-xs cursor-pointer font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 hover:scale-105 transition-transform"
            >
              <FaPhone className="text-primary" size={11} />
              {t("dateSelect.noSchedule.contactClinic")}
            </span>
          </div>
        </div>
      </Card>
    );
  }

  // Get day names array based on language
  const dayNamesKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const dayNames = dayNamesKeys.map((key) => t(`dateSelect.dayNames.${key}`));

  return (
    <div className="space-y-2 mx-auto">
      <h2 className="text-xl font-semibold text-textPrimary flex items-center gap-2">
        <FaRegCalendarDays className="text-primary" />
        {t("dateSelect.title")}
      </h2>

      {error && (
        <Alert message={error} type="error" showIcon className="mb-4" />
      )}

      {/* Calendar Container */}
      <div className="border border-textPrimary rounded-lg shadow-sm overflow-hidden max-w-xl mx-auto">
        {/* Calendar Header */}
        <div className="flex justify-between items-center p-3 bg-primary border-b border-textPrimary">
          <Button
            type="text"
            icon={<FaChevronLeft />}
            className="text-white"
            onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}
          />
          <div className="text-center">
            <div className="text-lg font-semibold text-white">
              {/* {currentMonth.format('MMMM YYYY')} */}
              {dayjs(currentMonth)
                .locale(currentLanguage === "ar" ? "ar" : "en")
                .format(
                  currentLanguage === "ar" ? "D MMMM YYYY" : "MMMM DD, YYYY",
                )}
            </div>
          </div>
          <Button
            type="text"
            icon={<FaChevronRight />}
            className="text-white"
            onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}
          />
        </div>

        {/* Day Names Header */}
        <div className="grid grid-cols-7 bg-gray-50">
          {dayNames.map((day, index) => (
            <div
              key={index}
              className="text-sm font-medium text-placeHolder text-center py-2 border border-textPrimary bg-cardColor"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 bg-white">
          {days.map((day, index) => {
            if (!day) {
              return (
                <div
                  key={index}
                  className="h-12 border border-textPrimary bg-gray-50"
                />
              );
            }

            const isSelected =
              selectedDate && dayjs(selectedDate).isSame(day, "day");
            const isToday = dayjs().isSame(day, "day");
            const isOtherMonth = !day.isSame(currentMonth, "month");

            const isDisabled = disabledDate(day);

            return (
              <div
                key={index}
                className={`h-12 flex items-center justify-center border cursor-pointer border-textPrimary
                ${
                  isSelected
                    ? " text-white"
                    : isDisabled
                      ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                      : isOtherMonth
                        ? "bg-gray-50 text-gray-400"
                        : "hover:bg-page "
                }
            `}
                onClick={() =>
                  !isDisabled && !isOtherMonth && handleDateSelect(day)
                }
              >
                <div
                  className={`flex items-center justify-center rounded-full w-8 h-8
                ${isSelected ? "bg-secondary text-white" : ""}`}
                >
                  {day.date()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
